import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  RefreshCw, Search, Loader2, Building2, Stethoscope, Filter, Images, X,
  ChevronLeft, ChevronRight, FileSearch, Download,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";

const API = window.location.origin + "/api";

const STATUS_OPTIONS = [
  { value: "open", label: "Aperte" },
  { value: "won", label: "Vinte" },
  { value: "lost", label: "Perse" },
  { value: "abandoned", label: "Abbandonate" },
  { value: "all", label: "Tutte" },
];

const STATUS_BADGE = {
  open: "bg-blue-50 text-blue-700 border-blue-200",
  won: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lost: "bg-rose-50 text-rose-700 border-rose-200",
  abandoned: "bg-slate-100 text-slate-600 border-slate-200",
};

const fmtDate = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

function PhotoLightbox({ photos, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx ?? 0);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % photos.length);
      else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [photos.length, onClose]);

  if (!photos?.length) return null;
  const current = photos[idx];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="report-photo-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
      >
        <X className="w-5 h-5" />
      </button>
      {photos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + photos.length) % photos.length); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % photos.length); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
      <img
        src={current.url}
        alt={current.filename || `foto-${idx}`}
        className="max-w-[94vw] max-h-[80vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/90 bg-black/50 px-3 py-1.5 rounded-full max-w-[90vw] truncate" title={current.filename}>
        {idx + 1} / {photos.length} · {current.filename || "foto"}
      </div>
    </div>
  );
}

function PhotoThumbs({ photos, onOpen }) {
  if (!photos?.length) {
    return <span className="text-xs text-slate-300 italic">—</span>;
  }
  const visible = photos.slice(0, 4);
  const remaining = photos.length - visible.length;
  return (
    <div className="flex items-center gap-1.5">
      {visible.map((p, i) => (
        <button
          key={p.url}
          type="button"
          onClick={() => onOpen(i)}
          title={p.filename}
          className="w-10 h-10 rounded-md overflow-hidden border border-slate-200 hover:border-indigo-400 transition-colors bg-slate-50 flex-shrink-0"
          data-testid={`report-photo-thumb-${i}`}
        >
          <img src={p.url} alt={p.filename} loading="lazy" className="w-full h-full object-cover" />
        </button>
      ))}
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => onOpen(visible.length)}
          className="w-10 h-10 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 text-xs font-semibold text-slate-600"
        >
          +{remaining}
        </button>
      )}
    </div>
  );
}

export default function ReportOpportunitaPage() {
  const [rows, setRows] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [pipelineFilter, setPipelineFilter] = useState(""); // single pipeline id, "" = all

  // Lightbox state
  const [lbPhotos, setLbPhotos] = useState(null);
  const [lbStartIdx, setLbStartIdx] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { status: statusFilter };
      if (pipelineFilter) params.pipeline_ids = pipelineFilter;
      const { data } = await axios.get(`${API}/appointments/opportunities-report`, { params });
      setRows(data?.rows || []);
      setPipelines(data?.pipelines || []);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || "Errore nel caricamento");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, pipelineFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.contact_name || "").toLowerCase().includes(q)
      || (r.pipeline_name || "").toLowerCase().includes(q)
      || (r.medico || "").toLowerCase().includes(q)
      || (r.stage_name || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totalPhotos = useMemo(
    () => filteredRows.reduce((sum, r) => sum + (r.photos_count || 0), 0),
    [filteredRows]
  );

  const exportCsv = () => {
    const header = ["Paziente","Pipeline","Stage","Stato","Medico","Assicurazione","Data inserimento","Importo","N. foto","File foto"];
    const lines = [header.join(",")];
    for (const r of filteredRows) {
      const photoNames = (r.photos || []).map((p) => p.filename).join(" | ");
      const row = [
        r.contact_name || "",
        r.pipeline_name || "",
        r.stage_name || "",
        r.status || "",
        r.medico || "",
        r.assicurazione || "",
        fmtDate(r.data_inserimento),
        r.monetary || 0,
        r.photos_count || 0,
        photoNames,
      ].map((v) => {
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      });
      lines.push(row.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `report-opportunita-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pt-[calc(7px+3.5rem+13px)] pb-10 px-4">
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Report Opportunità"
          subtitle="Elenco contenuti opportunità GHL — paziente, pipeline, foto allegate"
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={exportCsv}
                disabled={filteredRows.length === 0}
                title="Esporta CSV"
                aria-label="Esporta CSV"
                data-testid="report-export-csv"
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors backdrop-blur-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={fetchData}
                title="Aggiorna"
                aria-label="Aggiorna"
                data-testid="report-refresh-btn"
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors backdrop-blur-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          }
        />
      </div>

      <div className="max-w-7xl mx-auto mb-6 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            {/* Search */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca paziente, pipeline, medico, stage…"
                data-testid="report-search"
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white focus:outline-none focus:border-blue-400"
              />
            </div>
            {/* Pipeline filter */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <select
                value={pipelineFilter}
                onChange={(e) => setPipelineFilter(e.target.value)}
                data-testid="report-pipeline-filter"
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-blue-400"
              >
                <option value="">Tutte le pipeline ({pipelines.length})</option>
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            {/* Status filter */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="report-status-filter"
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-blue-400"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Counters */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1.5 bg-slate-100 rounded-lg px-3 py-1.5">
              <FileSearch className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs font-semibold text-slate-600" data-testid="report-total-count">
                {filteredRows.length}{search ? `/${rows.length}` : ""} opp
              </span>
            </div>
            <div className="flex items-center gap-1.5 bg-amber-100 rounded-lg px-3 py-1.5">
              <Images className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-xs font-semibold text-amber-700">{totalPhotos} foto</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-700 mb-4">
            {error}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-md p-12 text-center">
            <Loader2 className="w-8 h-8 text-slate-300 animate-spin mx-auto mb-3" />
            <p className="text-slate-400">Caricamento opportunità da GHL…</p>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-md p-12 text-center">
            <FileSearch className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-lg">Nessuna opportunità trovata</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            {/* Header */}
            <div className="hidden lg:grid grid-cols-[minmax(200px,1.4fr)_minmax(180px,1.2fr)_minmax(150px,1fr)_minmax(140px,1fr)_120px_minmax(220px,1.4fr)] gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              <div>Paziente</div>
              <div>Pipeline / Stage</div>
              <div>Medico</div>
              <div>Assicurazione</div>
              <div>Data</div>
              <div>Foto</div>
            </div>

            {filteredRows.map((r) => (
              <div
                key={r.opportunity_id}
                className="grid grid-cols-1 lg:grid-cols-[minmax(200px,1.4fr)_minmax(180px,1.2fr)_minmax(150px,1fr)_minmax(140px,1fr)_120px_minmax(220px,1.4fr)] gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors items-center"
                data-testid={`report-row-${r.opportunity_id}`}
              >
                {/* Paziente */}
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate" title={r.contact_name}>
                    {r.contact_name || "—"}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate" title={r.name}>{r.name}</div>
                </div>

                {/* Pipeline + Stage */}
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-700 truncate" title={r.pipeline_name}>
                    {r.pipeline_name || "—"}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className={`inline-block text-[10px] px-2 py-0.5 rounded border font-medium ${STATUS_BADGE[r.status] || STATUS_BADGE.open}`}>
                      {r.stage_name || r.status}
                    </span>
                  </div>
                </div>

                {/* Medico */}
                <div className="flex items-center gap-1.5 min-w-0">
                  <Stethoscope className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                  <span className="text-xs text-slate-600 truncate" title={r.medico}>
                    {r.medico || "—"}
                  </span>
                </div>

                {/* Assicurazione */}
                <div className="text-xs text-slate-600 truncate" title={r.assicurazione}>
                  {r.assicurazione || "—"}
                </div>

                {/* Data */}
                <div className="text-xs text-slate-600 tabular-nums">
                  {fmtDate(r.data_inserimento) || <span className="text-slate-300">—</span>}
                </div>

                {/* Foto */}
                <div>
                  <PhotoThumbs
                    photos={r.photos || []}
                    onOpen={(i) => { setLbPhotos(r.photos || []); setLbStartIdx(i); }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lbPhotos && (
        <PhotoLightbox
          photos={lbPhotos}
          startIdx={lbStartIdx}
          onClose={() => setLbPhotos(null)}
        />
      )}
    </div>
  );
}
